import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart, Search, Menu, X, Globe, ChevronDown, User } from 'lucide-react';
import { useCart } from '@/data/cart';
import { useI18n } from '@/i18n/I18nContext';
import { SUPPORTED_LOCALES, LOCALE_META, type Locale } from '@/i18n/translations';
import { prefetchRoute } from '@/lib/routePrefetch';
import sabaodyLogo from '@/assets/sabaody-logo-new.webp';

type NavItem = { label: string; path: string };
type NavGroup = { label: string; children: NavItem[] };
type NavEntry = NavItem | NavGroup;
const isGroup = (e: NavEntry): e is NavGroup => 'children' in e;

/** CSS-only animated dropdown wrapper (replaces framer-motion AnimatePresence) */
const DropdownPanel: React.FC<{ open: boolean; className?: string; children: React.ReactNode; role?: string; 'aria-label'?: string }> = ({ open, className = '', children, ...rest }) => (
  <div
    className={`transition-all duration-150 ease-out ${open ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-1 pointer-events-none'} ${className}`}
    {...rest}
  >
    {children}
  </div>
);

const Header: React.FC = () => {
  const { itemCount, setIsOpen } = useCart();
  const { t, locale, setLocale, localePath } = useI18n();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [localeDropdown, setLocaleDropdown] = useState(false);
  const [accessoriesOpen, setAccessoriesOpen] = useState(false);
  const [mobileAccessoriesOpen, setMobileAccessoriesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const accessoriesRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileToggleRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    /* Dynamically import supabase to keep vendor-supabase (~46 KB gz) out of the
       critical rendering path. The header paints immediately with a neutral state;
       the auth icon updates once the (local/cached) session check resolves. */
    let cancelled = false;
    import('@/integrations/supabase/client').then(({ supabase }) => {
      if (cancelled) return;
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (!cancelled) setIsLoggedIn(!!session?.user);
      });
      const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
        if (!cancelled) setIsLoggedIn(!!session?.user);
      });
      cleanupRef.current = () => subscription.unsubscribe();
    });
    return () => { cancelled = true; if (cleanupRef.current) cleanupRef.current(); };
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setLocaleDropdown(false);
      }
      if (accessoriesRef.current && !accessoriesRef.current.contains(e.target as Node)) {
        setAccessoriesOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Mobile menu: focus trap + Escape close + focus restore
  useEffect(() => {
    if (!mobileOpen) return;

    const menuEl = mobileMenuRef.current;
    if (!menuEl) return;

    const focusables = menuEl.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusables.length > 0) focusables[0].focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileOpen(false);
        mobileToggleRef.current?.focus();
        return;
      }
      if (e.key !== 'Tab') return;

      const els = menuEl.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (els.length === 0) return;
      const first = els[0];
      const last = els[els.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mobileOpen]);

  const NAV: NavEntry[] = [
    { label: t('nav.shop'), path: '/shop' },
    { label: t('nav.promo'), path: '/promo' },
    { label: t('nav.displays'), path: '/category/displays' },
    { label: t('nav.boosters'), path: '/category/boosters' },
    { label: t('nav.lots'), path: '/category/lots' },
    { label: t('nav.case'), path: '/category/case' },
    { label: t('nav.decks'), path: '/category/decks' },
    {
      label: t('nav.accessories' as any),
      children: [
        { label: t('nav.sleeves'), path: '/category/sleeves' },
        { label: t('nav.binder'), path: '/category/binder' },
        { label: t('nav.acrylic-protectors'), path: '/category/acrylic-protectors' },
        { label: t('nav.storage-boxes'), path: '/category/storage-boxes' },
      ],
    },
    { label: t('nav.loyalty'), path: '/loyalty' },
  ];

  const currentMeta = LOCALE_META[locale];

  const groups = SUPPORTED_LOCALES.reduce<Record<string, Locale[]>>((acc, l) => {
    const group = LOCALE_META[l].group;
    if (!acc[group]) acc[group] = [];
    acc[group].push(l);
    return acc;
  }, {});

  const isActivePath = (path: string) => {
    const localizedPath = localePath(path);
    return location.pathname === localizedPath || location.pathname === localizedPath + '/';
  };

  const isGroupActive = (group: NavGroup) => group.children.some(c => isActivePath(c.path));

  return (
    <header className="sticky top-0 z-50 glass border-b border-border/50">
      <div className="container flex items-center justify-between h-16 gap-4">
        {/* Logo */}
        <Link to={localePath('/')} className="flex items-center shrink-0">
          <img src={sabaodyLogo} alt="Sabaody Island" className="h-12 w-12" width={48} height={48} style={{ aspectRatio: '1/1' }} />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1" aria-label={t('a11y.main_navigation')}>
          {NAV.map((entry, i) =>
            isGroup(entry) ? (
              <div key={i} className="relative" ref={accessoriesRef}>
                <button
                  onClick={() => setAccessoriesOpen(!accessoriesOpen)}
                  aria-expanded={accessoriesOpen}
                  className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-primary/10 ${
                    isGroupActive(entry) ? 'text-primary bg-primary/5' : 'text-foreground/70'
                  }`}
                >
                  {entry.label}
                  <ChevronDown className={`w-3 h-3 transition-transform ${accessoriesOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                </button>
                <DropdownPanel
                  open={accessoriesOpen}
                  role="menu"
                  aria-label={t('nav.accessories' as any)}
                  className="absolute left-0 top-full mt-1 w-52 rounded-xl bg-popover text-popover-foreground border border-border shadow-elevated py-1 z-50"
                >
                  {entry.children.map(child => (
                    <Link
                      key={child.path}
                      to={localePath(child.path)}
                      role="menuitem"
                      onMouseEnter={() => prefetchRoute(child.path)}
                      onFocus={() => prefetchRoute(child.path)}
                      onClick={() => setAccessoriesOpen(false)}
                      className={`block px-4 py-2.5 text-sm transition-colors ${
                        isActivePath(child.path)
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'hover:bg-muted/50 text-foreground/80'
                      }`}
                    >
                      {child.label}
                    </Link>
                  ))}
                </DropdownPanel>
              </div>
            ) : (
              <Link
                key={entry.path}
                to={localePath(entry.path)}
                onMouseEnter={() => prefetchRoute(entry.path)}
                onFocus={() => prefetchRoute(entry.path)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-primary/10 ${
                  isActivePath(entry.path) ? 'text-primary bg-primary/5' : 'text-foreground/70'
                }`}
              >
                {entry.label}
              </Link>
            )
          )}
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-1.5">
          {/* Locale switcher */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setLocaleDropdown(!localeDropdown)}
              aria-expanded={localeDropdown}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold hover:bg-primary/10 transition-colors text-foreground/70"
              aria-label={`${t('locale.switch')}: ${currentMeta.label}`}
            >
              <Globe className="w-3.5 h-3.5" aria-hidden="true" />
              <span aria-hidden="true">{currentMeta.flag}</span>
              <span className="hidden sm:inline">{currentMeta.label}</span>
              <ChevronDown className={`w-3 h-3 transition-transform ${localeDropdown ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>
            <DropdownPanel
              open={localeDropdown}
              role="menu"
              aria-label={t('locale.switch')}
              className="absolute right-0 top-full mt-1 w-52 rounded-xl bg-popover text-popover-foreground border border-border shadow-elevated py-1 z-50"
            >
              {Object.entries(groups).map(([group, locales]) => (
                <div key={group}>
                  <div className="px-3 py-1.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wider" aria-hidden="true">
                    {group}
                  </div>
                  {locales.map(l => {
                    const meta = LOCALE_META[l];
                    const active = l === locale;
                    return (
                      <button
                        key={l}
                        role="menuitem"
                        onClick={() => { setLocale(l); setLocaleDropdown(false); }}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                          active ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted/50 text-foreground/80'
                        }`}
                      >
                        <span className="text-base" aria-hidden="true">{meta.flag}</span>
                        <span className="flex-1 text-left">{meta.label}</span>
                        {active && <span className="w-1.5 h-1.5 rounded-full bg-primary" aria-hidden="true" />}
                      </button>
                    );
                  })}
                </div>
              ))}
            </DropdownPanel>
          </div>

          <Link
            to={localePath(isLoggedIn ? '/account' : '/account/login')}
            className="p-2 rounded-xl hover:bg-primary/10 transition-colors"
            aria-label={isLoggedIn ? t('account.title') : t('auth.login')}
          >
            <User className={`w-5 h-5 ${isLoggedIn ? 'text-primary' : 'text-foreground/70'}`} aria-hidden="true" />
          </Link>

          <button
            onClick={() => setSearchOpen(!searchOpen)}
            className="p-2 rounded-xl hover:bg-primary/10 transition-colors"
            aria-label={t('nav.search')}
          >
            <Search className="w-5 h-5 text-foreground/70" aria-hidden="true" />
          </button>

          <button
            onClick={() => setIsOpen(true)}
            className="relative p-2 rounded-xl hover:bg-primary/10 transition-colors"
            aria-label={t('nav.cart')}
          >
            <ShoppingCart className="w-5 h-5 text-foreground/70" aria-hidden="true" />
            {itemCount > 0 && (
              <span
                className="absolute -top-1 -right-1 w-5 h-5 rounded-full gradient-ocean text-[11px] font-bold flex items-center justify-center text-primary-foreground animate-pop"
                aria-hidden="true"
              >
                {itemCount}
              </span>
            )}
          </button>

          <button
            ref={mobileToggleRef}
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 rounded-xl hover:bg-primary/10 transition-colors"
            aria-label={mobileOpen ? t('a11y.close_menu') : t('a11y.open_menu')}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X className="w-5 h-5" aria-hidden="true" /> : <Menu className="w-5 h-5" aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Search bar */}
      <div
        className={`overflow-hidden border-t border-border/30 transition-all duration-200 ${searchOpen ? 'max-h-20 opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="container py-3">
          <label htmlFor="site-search" className="sr-only">{t('a11y.search')}</label>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const q = searchQuery.trim();
              if (!q) return;
              setSearchOpen(false);
              navigate(`${localePath('/search')}?q=${encodeURIComponent(q)}`);
            }}
          >
            <input
              id="site-search"
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('search.placeholder')}
              className="w-full px-4 py-3 rounded-xl bg-muted/50 border border-border/50 focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm"
              autoFocus={searchOpen}
            />
          </form>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        ref={mobileMenuRef}
        className={`md:hidden overflow-hidden border-t border-border/30 transition-all duration-200 ${mobileOpen ? 'max-h-[80vh] opacity-100' : 'max-h-0 opacity-0'}`}
        role="dialog"
        aria-modal={mobileOpen ? "true" : undefined}
        aria-label={t('a11y.mobile_menu')}
      >
        <div className="container py-4 flex flex-col gap-1">
          {NAV.map((entry, i) =>
            isGroup(entry) ? (
              <div key={i}>
                <button
                  onClick={() => setMobileAccessoriesOpen(!mobileAccessoriesOpen)}
                  aria-expanded={mobileAccessoriesOpen}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                    isGroupActive(entry) ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
                  }`}
                >
                  {entry.label}
                  <ChevronDown className={`w-4 h-4 transition-transform ${mobileAccessoriesOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                </button>
                <div className={`overflow-hidden transition-all duration-200 ${mobileAccessoriesOpen ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
                  {entry.children.map(child => (
                    <Link
                      key={child.path}
                      to={localePath(child.path)}
                      onClick={() => setMobileOpen(false)}
                      className={`block pl-8 pr-4 py-2.5 rounded-xl text-sm transition-colors ${
                        isActivePath(child.path) ? 'text-primary font-medium' : 'text-foreground/70 hover:bg-muted'
                      }`}
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <Link
                key={entry.path}
                to={localePath(entry.path)}
                onClick={() => setMobileOpen(false)}
                className={`px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  isActivePath(entry.path) ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
                }`}
              >
                {entry.label}
              </Link>
            )
          )}
          <Link
            to={localePath(isLoggedIn ? '/account' : '/account/login')}
            onClick={() => setMobileOpen(false)}
            className="px-4 py-3 rounded-xl text-sm font-medium transition-colors hover:bg-muted flex items-center gap-2"
          >
            <User className="w-4 h-4" aria-hidden="true" />
            {isLoggedIn ? t('account.title') : t('auth.login_or_signup')}
          </Link>
        </div>
      </div>
    </header>
  );
};

export default Header;
